/**
 * Lock Manager - Gerenciamento centralizado e seguro do lock-document
 * 
 * Responsabilidades:
 * - Controlar estado do lock de forma centralizada
 * - Retry automático em operações de lock/unlock
 * - Force unlock para casos críticos
 * - Health check antes de operações importantes
 * - Listeners para feedback da UI
 */

import { lockDocument } from '../corelOperations';
import { sendToTerminal, terminalLog } from '../terminalLogger';

export type LockState = 'locked' | 'unlocked' | 'unknown' | 'error';

export interface LockResult {
  ok: boolean;
  locked: boolean;
  code?: string;
  error?: string;
  attempts?: number;
}

export interface LockHealth {
  isLocked: boolean;
  source: string | null; // 'current' ou null se não houver lock ativo
  duration: number; // ms desde que foi ativado
  lastError: string | null;
}

type StateChangeListener = (state: LockState) => void;

class LockManager {
  private currentState: LockState = 'unknown';
  private lockStartTime: number | null = null;
  private lastError: string | null = null;
  private lockSource: string | null = null;
  private listeners: Set<StateChangeListener> = new Set();
  private cleanupScheduled = false;

  constructor() {
    // Inicializar estado
    this.initialize();
  }

  private async initialize() {
    try {
      const health = await this.healthCheck();
      this.currentState = health.isLocked ? 'locked' : 'unlocked';
    } catch {
      this.currentState = 'unknown';
    }
  }

  /**
   * Verifica se o lock está ativo
   */
  isLocked(): boolean {
    return this.currentState === 'locked';
  }

  /**
   * Retorna o último erro ocorrido
   */
  getLastError(): string | null {
    return this.lastError;
  }

  /**
   * Bloqueia o documento com retry automático
   * 
   * @param motivo Motivo do bloqueio (ex: 'checklist', 'salvando')
   * @param maxRetries Número máximo de tentativas (padrão: 3)
   * @returns Resultado da operação
   */
  async lock(motivo: string = 'operacao', maxRetries: number = 3): Promise<LockResult> {
    sendToTerminal('🔒 [LOCK_MANAGER] Iniciando lock', { motivo, maxRetries });
    
    let lastError: string | undefined;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await lockDocument.iniciar(motivo);
        
        if (result.ok && result.locked === true) {
          // Sucesso
          this.currentState = 'locked';
          this.lockStartTime = Date.now();
          this.lockSource = motivo;
          this.lastError = null;
          this.notifyListeners('locked');
          
          sendToTerminal('✅ [LOCK_MANAGER] Lock ativado com sucesso', {
            motivo,
            attempt,
            source: this.lockSource
          });
          
          return {
            ok: true,
            locked: true,
            attempts: attempt
          };
        }
        
        // Falha mas não lançou exceção
        lastError = result.code || 'Lock não confirmado pelo backend';
        
        if (attempt < maxRetries) {
          const delay = attempt * 500; // Backoff progressivo
          sendToTerminal(`⚠️ [LOCK_MANAGER] Tentativa ${attempt}/${maxRetries} falhou, retry em ${delay}ms`, {
            code: result.code
          });
          await new Promise(r => setTimeout(r, delay));
        }
        
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Erro desconhecido';
        
        if (attempt < maxRetries) {
          const delay = attempt * 500;
          sendToTerminal(`❌ [LOCK_MANAGER] Exceção na tentativa ${attempt}/${maxRetries}, retry em ${delay}ms`, {
            error: lastError
          });
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    
    // Todas as tentativas falharam
    this.currentState = 'error';
    this.lastError = lastError || 'Falha ao bloquear documento';
    this.notifyListeners('error');
    
    sendToTerminal('❌ [LOCK_MANAGER] Lock falhou após todas as tentativas', {
      motivo,
      maxRetries,
      error: this.lastError
    });
    
    return {
      ok: false,
      locked: false,
      error: this.lastError,
      attempts: maxRetries
    };
  }

  /**
   * Desbloqueia o documento com retry automático
   * 
   * @param maxRetries Número máximo de tentativas (padrão: 3)
   * @returns Resultado da operação
   */
  async unlock(maxRetries: number = 3): Promise<LockResult> {
    sendToTerminal('🔓 [LOCK_MANAGER] Iniciando unlock', { maxRetries });
    
    let lastError: string | undefined;
    let lastCode: string | undefined;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await lockDocument.parar();
        
        // Lock já estava inativo - OK
        if (result.code === 'lock_not_active') {
          this.currentState = 'unlocked';
          this.lockStartTime = null;
          this.lockSource = null;
          this.lastError = null;
          this.notifyListeners('unlocked');
          
          sendToTerminal('✅ [LOCK_MANAGER] Lock já estava inativo', { attempt });
          
          return {
            ok: true,
            locked: false,
            code: 'lock_not_active',
            attempts: attempt
          };
        }
        
        // Unlock bem-sucedido
        if (result.ok && result.locked === false) {
          this.currentState = 'unlocked';
          this.lockStartTime = null;
          this.lockSource = null;
          this.lastError = null;
          this.notifyListeners('unlocked');
          
          sendToTerminal('✅ [LOCK_MANAGER] Unlock realizado com sucesso', { attempt });
          
          return {
            ok: true,
            locked: false,
            attempts: attempt
          };
        }
        
        // Resposta ambígua
        lastError = result.code || 'Unlock não confirmado pelo backend';
        lastCode = result.code;
        
        if (attempt < maxRetries) {
          const delay = attempt * 500; // Backoff progressivo
          sendToTerminal(`⚠️ [LOCK_MANAGER] Tentativa ${attempt}/${maxRetries} falhou, retry em ${delay}ms`, {
            code: result.code,
            locked: result.locked
          });
          await new Promise(r => setTimeout(r, delay));
        }
        
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Erro desconhecido';
        
        if (attempt < maxRetries) {
          const delay = attempt * 500;
          sendToTerminal(`❌ [LOCK_MANAGER] Exceção na tentativa ${attempt}/${maxRetries}, retry em ${delay}ms`, {
            error: lastError
          });
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    
    // Todas as tentativas falharam
    this.currentState = 'error';
    this.lastError = lastError || 'Falha ao desbloquear documento';
    this.notifyListeners('error');
    
    sendToTerminal('❌ [LOCK_MANAGER] Unlock falhou após todas as tentativas', {
      maxRetries,
      error: this.lastError,
      code: lastCode
    });
    
    return {
      ok: false,
      locked: true,
      error: this.lastError,
      code: lastCode,
      attempts: maxRetries
    };
  }

  /**
   * Força o desbloqueio do documento (última linha de defesa)
   * Tenta múltiplas estratégias agressivas
   * 
   * @returns true se bem-sucedido
   */
  async forceUnlock(): Promise<boolean> {
    sendToTerminal('⚡ [LOCK_MANAGER] Forçando unlock agressivo');
    
    try {
      // Estratégia 1: Unlock com 5 tentativas
      const result = await this.unlock(5);
      if (result.ok) {
        sendToTerminal('✅ [LOCK_MANAGER] Force unlock bem-sucedido (estratégia 1)');
        return true;
      }
      
      // Estratégia 2: Aguardar mais tempo e tentar novamente
      sendToTerminal('⚠️ [LOCK_MANAGER] Estratégia 1 falhou, tentando estratégia 2');
      await new Promise(r => setTimeout(r, 2000));
      
      const result2 = await this.unlock(3);
      if (result2.ok) {
        sendToTerminal('✅ [LOCK_MANAGER] Force unlock bem-sucedido (estratégia 2)');
        return true;
      }
      
      // Estratégia 3: Assumir que está desbloqueado (otimista)
      sendToTerminal('⚠️ [LOCK_MANAGER] Todas estratégias falharam, assumindo unlock');
      this.currentState = 'unknown';
      this.lockStartTime = null;
      this.lockSource = null;
      this.notifyListeners('unknown');
      
      return false;
      
    } catch (error) {
      sendToTerminal('❌ [LOCK_MANAGER] Erro crítico no force unlock', {
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }

  /**
   * Verifica o estado de saúde do lock
   * 
   * @returns Informações de saúde do lock
   */
  async healthCheck(): Promise<LockHealth> {
    try {
      // Tentar verificar estado real via lockDocument
      // Nota: lockDocument.parar() retorna lock_not_active se não houver lock
      const testResult = await lockDocument.parar();
      
      const isLocked = testResult.ok === false && testResult.code !== 'lock_not_active';
      const duration = isLocked && this.lockStartTime 
        ? Date.now() - this.lockStartTime 
        : 0;
      
      return {
        isLocked,
        source: isLocked ? this.lockSource : null,
        duration,
        lastError: this.lastError
      };
      
    } catch (error) {
      terminalLog.warn('[LOCK_MANAGER] Erro no health check', {
        error: error instanceof Error ? error.message : String(error)
      });
      
      return {
        isLocked: this.currentState === 'locked',
        source: this.lockSource,
        duration: this.lockStartTime ? Date.now() - this.lockStartTime : 0,
        lastError: this.lastError
      };
    }
  }

  /**
   * Agenda limpeza futura do lock
   * Útil quando unlock falha mas queremos prosseguir
   */
  scheduleCleanup(): void {
    if (this.cleanupScheduled) return;
    
    this.cleanupScheduled = true;
    sendToTerminal('📅 [LOCK_MANAGER] Limpeza agendada para 30s');
    
    setTimeout(async () => {
      sendToTerminal('🧹 [LOCK_MANAGER] Executando limpeza agendada');
      await this.unlock(3);
      this.cleanupScheduled = false;
    }, 30000);
  }

  /**
   * Registra listener para mudanças de estado
   * 
   * @param callback Função a ser chamada quando estado mudar
   * @returns Função para cancelar o listener
   */
  onStateChange(callback: StateChangeListener): () => void {
    this.listeners.add(callback);
    
    // Chamar imediatamente com estado atual
    callback(this.currentState);
    
    // Retornar função de cleanup
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notifica todos os listeners sobre mudança de estado
   */
  private notifyListeners(newState: LockState): void {
    this.listeners.forEach(listener => {
      try {
        listener(newState);
      } catch (error) {
        terminalLog.error('[LOCK_MANAGER] Erro no listener', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    });
  }

  /**
   * Reset manual do estado (para casos extremos)
   */
  resetState(): void {
    sendToTerminal('🔄 [LOCK_MANAGER] Reset manual do estado');
    this.currentState = 'unknown';
    this.lockStartTime = null;
    this.lockSource = null;
    this.lastError = null;
    this.notifyListeners('unknown');
  }
}

// Singleton global
export const lockManager = new LockManager();

